import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import {
  Column,
  device,
  RadioButton,
  Row,
  Segment,
  useMedia,
} from '@landsbankinn/ui';
import { LandsbankinnContext, useNextQueryParam } from '@landsbankinn/utils';
import { useRouter } from 'next/router';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type {
  AtmType,
  BranchServices,
} from '../../../__generated__/globalTypes';
import { Region, ServicePointType } from '../../../__generated__/globalTypes';
import type {
  FetchServicePoints,
  FetchServicePointsVariables,
  FetchServicePoints_servicePoints_nodes,
} from '../../graphql/__generated__/FetchServicePoints';
import { FETCH_SERVICE_POINTS } from '../../graphql/fetchServicePoints';

import { AtmTypesFilter } from './filters/AtmFilter';
import { BranchServicesFilter } from './filters/BranchServicesFilter';
import { RegionDropdown } from './filters/RegionDropdown';
import { LoadingCards } from './LoadingCards';
import { MapBox } from './Map/MapBox';
import { NotFound } from './notFound/NotFound';
import { Locations } from './servicePointCard/servicePointCard';
import res from './ServicePoints.res';

const CardsSegment = styled(Segment)`
  background: rgb(244, 247, 247);
`;

const StyledColumn = styled(Column)`
  margin-bottom: 20px;
  @media ${device.tablet} {
    margin-bottom: 0px;
  }
`;

const StyledRow = styled(Row)`
  margin-bottom: 40px;
  > {
    margin-bottom: 20px;
  }
`;

export interface IAtm extends Omit<FetchServicePoints, 'atmType'> {
  atmType: AtmType[];
  currencyAtm?: FetchServicePoints;
  coinCounterAtm?: FetchServicePoints;
  coinRollAtm?: FetchServicePoints;
}

interface ServicePointsContext {
  isMapOpen: boolean;
  selectedType: ServicePointType;
  setIsMapOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedServicePoint?: React.Dispatch<
    React.SetStateAction<FetchServicePoints_servicePoints_nodes | undefined>
  >;
}

const ServicePointsContext = createContext<ServicePointsContext>({
  isMapOpen: false,
  selectedType: ServicePointType.Atm,
});

export function ServicePoints() {
  const { r, locale } = useResource();
  const router = useRouter();
  const type = useNextQueryParam('type', router.asPath);

  const [selectedId, setSelectedId] = useState<ServicePointType>(
    ServicePointType.Branch,
  );

  useEffect(() => {
    if (type === 'hradbankar') {
      setSelectedId(ServicePointType.Atm);
    }
  }, [type]);

  const isMobile = useMedia('sm');
  const [branchServices, setBranchServices] = React.useState<BranchServices[]>(
    [],
  );

  const [selectedAtmTypes, setSelectedAtmTypes] = React.useState<AtmType[]>([]);

  const [selectedRegion, setSelectedRegion] = useState<Region>(Region.All);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const [selectedServicePoint, setSelectedServicePoint] =
    useState<FetchServicePoints_servicePoints_nodes>();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // allow global access to the map for the link resolver
      // as it's `onClick` handler is outside of the React Context.
      (window as any).serviceLocationMap = {
        setIsMapOpen,
      };
    }
  }, []);

  const { client } = useContext(LandsbankinnContext);
  const { data, loading } = useQuery<
    FetchServicePoints,
    FetchServicePointsVariables
  >(FETCH_SERVICE_POINTS, {
    client,
    context: {
      headers: {
        'Accept-Language': locale === 'is' ? 'is' : 'en-GB',
      },
    },
    variables: {
      service: selectedId,
      region: selectedRegion,
      branchServices:
        selectedId === ServicePointType.Branch &&
        branchServices &&
        branchServices.length > 0
          ? branchServices
          : null,
      atmTypes:
        selectedId === ServicePointType.Atm &&
        selectedAtmTypes &&
        selectedAtmTypes.length > 0
          ? selectedAtmTypes
          : null,
    },
  });

  return (
    <ServicePointsContext.Provider
      value={{
        isMapOpen,
        setIsMapOpen,
        setSelectedServicePoint,
        selectedType: selectedId,
      }}
    >
      <>
        <CardsSegment>
          <StyledRow>
            <StyledColumn md={{ width: 4 / 12 }} sm={{ width: 1 / 1 }}>
              <RegionDropdown
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
              />
            </StyledColumn>

            <StyledColumn md={{ width: 4 / 12 }} sm={{ width: 1 / 1 }}>
              <RadioButton
                checked={selectedId === ServicePointType.Branch}
                label={r(res.branches)}
                onChange={() => {
                  setSelectedId(ServicePointType.Branch);
                }}
                theme="primary"
                variant="noBorder"
              />
            </StyledColumn>
            <Column md={{ width: 4 / 12 }} sm={{ width: 1 / 1 }}>
              <RadioButton
                checked={selectedId === ServicePointType.Atm}
                label={r(res.atms)}
                onChange={() => {
                  setSelectedId(ServicePointType.Atm);
                }}
                theme="primary"
                variant="noBorder"
              />
            </Column>
          </StyledRow>

          {selectedId === ServicePointType.Branch && (
            <BranchServicesFilter
              selectedServices={branchServices}
              setBranchServices={setBranchServices}
            />
          )}

          {selectedId === ServicePointType.Atm && (
            <AtmTypesFilter
              selectedAtmTypes={selectedAtmTypes}
              setSelectedAtmTypes={setSelectedAtmTypes}
            />
          )}

          {loading && <LoadingCards />}

          {!loading &&
            data?.servicePoints &&
            data.servicePoints.totalCount > 0 && (
              <Locations locations={data.servicePoints.nodes} />
            )}
          {!loading && data?.servicePoints.totalCount === 0 && <NotFound />}
        </CardsSegment>
        {isMapOpen && data?.servicePoints.nodes && (
          <MapBox
            locations={data.servicePoints.nodes}
            selectedType={selectedId}
            isMobile={isMobile}
            defaultServiceLocation={selectedServicePoint}
          />
        )}
      </>
    </ServicePointsContext.Provider>
  );
}
export const useServiceLocationsContext = () =>
  useContext(ServicePointsContext);
